import {
    Component,
    Input,
    OnChanges,
    output,
    ChangeDetectionStrategy
} from "@angular/core";

import { NgClass } from "@angular/common";
import { NgbAccordionCollapse, NgbAccordionDirective, NgbAccordionItem, NgbCollapse } from "@ng-bootstrap/ng-bootstrap";
import moment from "moment";
import {
    DateDrillDownDay,
    DateDrillDownMonth,
    DateDrillDownPeriod,
    DateDrillDownWeek,
    IDateDrillDownBindings
} from "./models/dateDrillDownItem";
import { PeriodType } from "./models/periodType";

@Component({
    selector: "rask-date-drill-down-selector",
    templateUrl: "./date-drill-down-selector.component.html",
    styleUrls: ["./date-drill-down-selector.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        NgbAccordionDirective,
        NgbAccordionItem,
        NgClass,
        NgbCollapse,
        NgbAccordionCollapse
    ]
})
export class DateDrillDownSelectorComponent implements OnChanges, IDateDrillDownBindings {
    period: DateDrillDownPeriod;
    months: DateDrillDownMonth[];

    // binding implementation
    @Input() periodFrom: string;
    @Input() periodTom: string;

    @Input() dateDepth: string;
    @Input() enableEditPeriod: boolean;
    @Input() onEditPeriod: () => any;
    @Input() onClose: () => any;

    dateSelected = output<any>();
    dateDrilldownPickerToggled = output();

    startDate: Date;
    endDate: Date;

    ngOnChanges(changesObj: any) {
        if (changesObj.periodFrom !== undefined) {
            this.startDate = new Date(changesObj.periodFrom.currentValue);
        }
        if (changesObj.periodTom !== undefined) {
            this.endDate = new Date(changesObj.periodTom.currentValue);
        }
        if (changesObj.dateDepth !== undefined) {
            this.dateDepth = changesObj.dateDepth.currentValue !== undefined ? changesObj.dateDepth.currentValue : PeriodType.week;
        }
        this.setup();
    }

    stopPropagation($event: any) {
        const target = $event.target.id;
        if (target.includes("btnMonth_")) {
            $event.stopPropagation();
        }
    }

    onDateSelected(param: any) {
        this.dateSelected.emit(param);
    }

    toggleDateDrilldownPicker() {
        this.dateDrilldownPickerToggled.emit();
    }

    setupWeeks(month: DateDrillDownMonth): DateDrillDownWeek[] {
        const weekNumbers: string[] = [];
        const weeks: DateDrillDownWeek[] = [];

        for (let date = month.startDate; date <= month.endDate; date = this.addDaysToDate(date, 1)) {
            const week = moment(date).format("W");
            if (weekNumbers.indexOf(week) < 0) {
                weekNumbers.push(week);
                const weekItem = new DateDrillDownWeek(this);
                weekItem.label = `v ${week}`;

                weekItem.startDate = moment(date).startOf("isoWeek").toDate();
                weekItem.endDate = moment(date).endOf("isoWeek").toDate();

                weekItem.startDateActive = this.copyDateWithoutTime(date);
                weekItem.endDateActive = month.endDate < weekItem.endDate ? month.endDate : weekItem.endDate;

                if (weeks.length > 0) {
                    const lastWeek = weeks[weekNumbers.indexOf(week) - 1];
                    lastWeek.endDateActive = this.addDaysToDate(date, -1);
                }

                weeks.push(weekItem);
            }
        }

        return weeks;
    }

    setupDays(week: DateDrillDownWeek): DateDrillDownDay[] {
        const periodStartDate = this.copyDateWithoutTime(this.startDate);
        const periodEndDate = this.copyDateWithoutTime(this.endDate);
        const days: DateDrillDownDay[] = [];
        for (let date = week.startDate; date <= week.endDate; date = this.addDaysToDate(date, 1)) {
            const dayItem = new DateDrillDownDay(this);
            const day = date.toLocaleString("sv", { weekday: "short" });
            dayItem.label = `${this.capitilizeString(day)} ${date.getDate()}/${date.getMonth() + 1}`;
            dayItem.startDate = date;
            dayItem.endDate = date;
            dayItem.outsidePeriod = date < week.startDateActive || date > week.endDateActive;
            dayItem.disabled = date < periodStartDate || date > periodEndDate;
            days.push(dayItem);
        }

        return days;
    }

    nextMonth = (date: Date, dayInMonth: number) => moment(date).add(1, "month").date(dayInMonth).toDate();

    inactivateAll() {
        this.period.active = false;
        (this.months ?? []).forEach(month => {
            month.active = false;
            (month.weeks ?? []).forEach(week => {
                week.active = false;
                (week.days ?? []).forEach(day => {
                    day.active = false;
                });
            });
        });
    }

    private setupMonths(): DateDrillDownMonth[] {
        const monthNames: string[] = [];
        const months: DateDrillDownMonth[] = [];

        for (let date = this.startDate; date <= this.endDate; date = this.nextMonth(date, 1)) {
            const monthName = date.toLocaleString("sv", { month: "long" });

            if (monthNames.indexOf(date.getFullYear() + monthName) < 0) {
                monthNames.push(date.getFullYear() + monthName);
                const monthItem = new DateDrillDownMonth(this);
                monthItem.label = this.capitilizeString(monthName);
                monthItem.startDate = new Date(date.getFullYear(), date.getMonth(), 1);
                if (monthItem.startDate < this.startDate) {
                    monthItem.startDate = this.copyDateWithoutTime(this.startDate);
                }

                monthItem.endDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);
                if (monthItem.endDate > this.endDate) {
                    monthItem.endDate = this.copyDateWithoutTime(this.endDate);
                }

                months.push(monthItem);
            }
        }

        return months;
    }

    private setup() {
        if (this.validate()) {
            this.period = this.setupPeriod();
            this.months = this.setupMonths();
            this.period.active = true;
        }
    }

    private validate(): boolean {
        if (this.startDate && !(this.startDate instanceof Date)) {
            throw new Error("invalid startDate");
        }

        if (this.endDate && !(this.endDate instanceof Date)) {
            throw new Error("invalid endDate");
        }

        if (this.dateDepth && PeriodType.periodTypes.indexOf(this.dateDepth) === -1) {
            throw new Error("invalid dateDepth");
        }

        return true;
    }

    private setupPeriod(): DateDrillDownPeriod {
        const period = new DateDrillDownPeriod(this);
        let startPeriod = new Date(this.startDate.valueOf()).toLocaleString("sv", { month: "short",
            year: "numeric" });
        let endPeriod = new Date(this.endDate.valueOf()).toLocaleString("sv", { month: "short",
            year: "numeric" });
        startPeriod = this.capitilizeString(startPeriod);
        endPeriod = this.capitilizeString(endPeriod);
        period.startDate = this.startDate;
        period.endDate = this.endDate;
        period.label = `${startPeriod} - ${endPeriod}`;

        return period;
    }

    private capitilizeString(str: string): string {
        const trimmed = str.replace(/\u200E/, ""); // Fix för UTF-16 space i IE
        return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
    }

    private copyDateWithoutTime(date: Date): Date {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    }

    private addDaysToDate(date: Date, days: number): Date {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
    }
}
